import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Activity,
  KanbanSquare,
  Download,
  Shield,
  Building2,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import { Logo } from '../Logo.js';
import { SearchTrigger } from './SearchTrigger.js';
import { NotificationBell } from './NotificationBell.js';
import { UserMenu } from './UserMenu.js';
import { cn } from '../../lib/cn.js';

interface NavTab {
  to: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
  managerOnly?: boolean;
  adminOnly?: boolean;
}

const NAV_TABS: NavTab[] = [
  {
    to: '/',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-3.5 h-3.5" />,
    exact: true,
  },
  {
    to: '/landlords',
    label: 'Landlords',
    icon: <Users className="w-3.5 h-3.5" />,
  },
  {
    to: '/activity',
    label: 'Activity',
    icon: <Activity className="w-3.5 h-3.5" />,
  },
  {
    to: '/kanban',
    label: 'Board',
    icon: <KanbanSquare className="w-3.5 h-3.5" />,
  },
  {
    to: '/exports',
    label: 'Exports',
    icon: <Download className="w-3.5 h-3.5" />,
    managerOnly: true,
  },
  {
    to: '/buildings',
    label: 'Buildings',
    icon: <Building2 className="w-3.5 h-3.5" />,
  },
  {
    to: '/admin/users',
    label: 'Admin',
    icon: <Shield className="w-3.5 h-3.5" />,
    adminOnly: true,
  },
];

interface TopNavProps {
  onMenuToggle?: () => void;
  menuOpen?: boolean;
}

export function TopNav({ onMenuToggle, menuOpen }: TopNavProps) {
  const { hasRole } = useAuth();
  const router = useRouterState();
  const current = router.location.pathname;

  const visibleTabs = NAV_TABS.filter((tab) => {
    if (tab.adminOnly && !hasRole('admin')) return false;
    if (tab.managerOnly && !hasRole('manager')) return false;
    return true;
  });

  return (
    <header className="fixed top-0 left-0 right-0 z-30 px-3 py-2 bg-hover-bg border-b border-border">
      {/* Outer white card container */}
      <div className="bg-white rounded-xl border border-border shadow-card flex items-center gap-2 px-3 h-11">
        {/* Mobile: hamburger + logo */}
        <div className="flex sm:hidden items-center gap-2 flex-1">
          <button
            className="p-2 rounded-lg hover:bg-hover-bg min-h-[36px] min-w-[36px] flex items-center justify-center text-secondary"
            onClick={onMenuToggle}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <Link to="/" className="flex-shrink-0" aria-label="Imza home">
            <Logo size={32} />
          </Link>
        </div>

        {/* Desktop: pill tabs */}
        <nav
          className="hidden sm:flex items-center gap-0.5 flex-1"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link to="/" className="mr-4 flex-shrink-0" aria-label="Imza home">
            <Logo size={32} />
          </Link>

          {visibleTabs.map((tab) => {
            const isActive = tab.exact ? current === tab.to : current.startsWith(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  isActive
                    ? 'bg-white shadow-sm border border-border text-primary-text'
                    : 'text-secondary hover:text-primary-text hover:bg-hover-bg'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={isActive ? 'text-primary' : ''}>{tab.icon}</span>
                {tab.label}
              </Link>
            );
          })}
        </nav>

        {/* Right cluster: search + bell + user */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <SearchTrigger />
          <NotificationBell />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
